import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import PasswordInput from '../components/PasswordInput';

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const token = params.get('token');

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError('Missing invite token');
      setLoading(false);
      return;
    }
    fetch(`/api/invites/${token}`)
      .then(async (res) => {
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || 'Invite not found');
        setInvite(body);
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`/api/invites/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Failed to accept invite');

      await signIn(body.email, password);
      navigate('/');
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <div className="flex-1 flex items-center justify-center bg-white px-6 py-12 lg:py-0 lg:w-1/2">
        <div className="w-full max-w-[400px]">
          <div className="text-center mb-6">
            <img
              src="/cscore-logo.png"
              alt="CScore"
              className="h-10 mx-auto"
              style={{ filter: 'brightness(0) saturate(100%) invert(13%) sepia(90%) saturate(5500%) hue-rotate(270deg) brightness(80%) contrast(110%)' }}
            />
          </div>

          {loading ? (
            <p className="text-sm text-gray-500 text-center">Loading invite...</p>
          ) : loadError ? (
            <div className="text-center">
              <h2 className="text-[28px] font-semibold text-text mb-3">Invite Unavailable</h2>
              <p className="text-sm text-gray-600 mb-6">{loadError}</p>
              <Link to="/login" className="text-primary font-medium text-sm hover:underline">
                Back to Sign In
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-[28px] font-semibold text-text text-center mb-2">Accept Invite</h2>
              <p className="text-sm text-gray-600 text-center mb-6">
                Hi {invite.first_name || 'there'}, set a password to join{' '}
                <strong>{invite.clinic_name || 'your clinic'}</strong>.
              </p>

              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-text mb-1.5">Email</label>
                  <input
                    type="email"
                    value={invite.email}
                    disabled
                    className="w-full border border-[#E6E6E6] rounded-lg px-4 py-3 text-sm bg-gray-50 text-gray-600"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-text mb-1.5">Password</label>
                  <PasswordInput
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoFocus
                    className="w-full border border-[#E6E6E6] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30 focus:border-primary-30"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-text mb-1.5">Confirm Password</label>
                  <PasswordInput
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="w-full border border-[#E6E6E6] rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-30 focus:border-primary-30"
                  />
                </div>

                {error && (
                  <div className="mb-3 p-2 bg-destructive-10 border border-destructive-30 rounded-lg text-sm text-destructive">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-primary text-white font-medium text-sm py-3 rounded-lg hover:bg-primary-80 disabled:opacity-50"
                >
                  {submitting ? 'Setting up your account...' : 'Set Password & Sign In'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
      <div
        className="hidden lg:flex lg:w-1/2 flex-col items-center justify-center px-12"
        style={{ background: 'radial-gradient(ellipse farthest-corner at 10% 10%, #7F31DB, #E1C2FA)' }}
      >
        <h2 className="text-white text-4xl font-bold mb-4 text-center">Measure What Matters</h2>
        <p className="text-white/80 text-lg text-center max-w-md leading-relaxed">
          Track clinician performance, improve patient outcomes, and make data-driven decisions for your treatment center.
        </p>
      </div>
    </div>
  );
}
